import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getPtDischargeNoteForm,
  updatePtDischargeNoteForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CommentsIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HelpingIcon,
  HomeIcon,
  LogOutIcon,
  MedicalNotesIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  PT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  BALANCE_STATUS_OPTIONS,
  BED_MOBILITY_OPTIONS,
  DISCHARGED_TO_OPTIONS,
  DISCHARGE_REASON_OPTIONS,
  DISCHARGE_STATUS_PARAMETER_OPTIONS,
  EDUCATION_TOPIC_OPTIONS,
  FALL_RISK_OPTIONS,
  FOLLOW_UP_OPTIONS,
  GOAL_STATUS_OPTIONS,
  HEP_UNDERSTANDING_OPTIONS,
  LEARNER_OPTIONS,
  PT_DISCHARGE_ARRAY_KEYS,
  PT_INTERVENTION_OPTIONS,
  ROM_STATUS_OPTIONS,
  THERAPY_GOAL_ROWS,
  TRANSFER_ASSIST_OPTIONS,
  TRANSFER_TYPE_OPTIONS,
  YES_NO_NOT_REQUIRED_OPTIONS,
  YES_NO_OPTIONS,
  emptyGoalStatuses,
  emptyPtDischargePayload,
  emptyStatusComparisonRows,
  toggleInList,
  today,
} from "./ptDischargeNoteContent";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "./ptVisitCommon";
import PtNotesCommonHeader from "./PtNotesCommonHeader";
import PainScalePills from "./PainScalePills";
import {
  normalizePtCommonHeaderState,
  resolvePtNotesCommonHeader,
  withNormalizedPtCommonHeader,
} from "./ptNotesCommonHeaderUtils";

const DISCHARGE_DATE_MAX = new Date().toISOString().slice(0, 10);

/** Legacy nursing / SN discharge fields that must not persist on PT discharge. */
const NURSE_LEFTOVER_KEYS = [
  "dischargeReasons",
  "skinIntegrity",
  "woundCurrentStatus",
  "woundDrainage",
  "woundComments",
  "woundLocation",
  "woundSize",
  "mentalStatus",
  "mentalStatusComments",
  "respiratoryFindings",
  "respiratoryOther",
  "respiratoryStatus",
  "cardioRhythm",
  "edema",
  "edemaLocationSeverity",
  "giAppetite",
  "giStatus",
  "giOther",
  "giComments",
  "guStatus",
  "guComments",
  "finalMedListProvided",
  "finalEducationProvided",
  "diseaseConditions",
  "patientUnderstanding",
  "mobility",
  "mobilityComments",
  "fallPreventionEducationOptions",
  "vitalsBp",
  "vitalsHr",
  "vitalsRr",
  "vitalsTemp",
  "vitalsSpo2",
  "vitalsWeight",
  "vitalsBpSys",
  "vitalsBpDia",
  "vitalsPain",
  "primaryLanguage",
  "mrn",
  "dateOfBirth",
  "skilledNursingNeed",
];

const emptyValues = () => ({
  ...emptyPtDischargePayload(),
  dischargeDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const normalizeExclusiveYesNo = (value) => {
  if (Array.isArray(value)) {
    if (value.includes("Yes")) return "Yes";
    if (value.includes("No")) return "No";
    return "";
  }
  const s = String(value || "").trim();
  if (s === "Yes" || s === "No") return s;
  return "";
};

const normalizeExclusiveYesNoNotRequired = (value) => {
  if (Array.isArray(value)) {
    if (value.includes("Yes")) return "Yes";
    if (value.includes("No")) return "No";
    if (value.includes("Not Required")) return "Not Required";
    return "";
  }
  const s = String(value || "").trim();
  if (YES_NO_NOT_REQUIRED_OPTIONS.includes(s)) return s;
  return "";
};

const parsePain = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  if (rounded < 0 || rounded > 10) return null;
  return rounded;
};

const normalizeStatusComparison = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const byParameter = Object.fromEntries(
    list
      .filter((row) => row && row.parameter)
      .map((row) => [row.parameter, row]),
  );
  return DISCHARGE_STATUS_PARAMETER_OPTIONS.map((parameter) => {
    const existing = byParameter[parameter] || {};
    return {
      parameter,
      initialBaseline: String(existing.initialBaseline || "").trim(),
      dischargeCurrent: String(existing.dischargeCurrent || "").trim(),
      status: String(existing.status || "").trim(),
    };
  });
};

const normalizeDischargeReason = (loadedPayload) => {
  const direct = String(loadedPayload.dischargeReason || "").trim();
  if (direct && DISCHARGE_REASON_OPTIONS.includes(direct)) return direct;
  if (direct) return direct;

  const legacy = loadedPayload.dischargeReasons;
  if (Array.isArray(legacy) && legacy.length > 0) {
    const match = legacy
      .map((item) => String(item || "").trim())
      .find((item) => DISCHARGE_REASON_OPTIONS.includes(item));
    if (match) return match;
    return String(legacy[0] || "").trim();
  }
  return "";
};

const normalizeExclusiveOption = (value, options) => {
  if (Array.isArray(value)) {
    const match = value
      .map((item) => String(item || "").trim())
      .find((item) => options.includes(item));
    return match || "";
  }
  const s = String(value || "").trim();
  return options.includes(s) ? s : s;
};

const stripNurseLeftovers = (payload) => {
  const next = { ...(payload || {}) };
  NURSE_LEFTOVER_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

const PtDischargeNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const buildPatientPrefill = useCallback(() => {
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];
    const soc = latestEpisode?.socDate || latestEpisode?.startDate || "";

    return {
      ...emptyPtDischargePayload(),
      socDate: soc ? formatDateForInput(soc) : "",
    };
  }, [patient]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyPtDischargePayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickPtVisitMeta(loadedPayload, data),
        dischargeDate:
          data.dischargeDate || loadedPayload.dischargeDate || today(),
        socDate: String(
          loadedPayload.socDate || base.socDate || "",
        ).trim(),
        commonHeader: normalizePtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        dischargeReason: normalizeDischargeReason(loadedPayload),
        dischargeReasonOther: String(
          loadedPayload.dischargeReasonOther || "",
        ).trim(),
        subjectiveReport: String(loadedPayload.subjectiveReport || "").trim(),
        statusComparison: normalizeStatusComparison(
          loadedPayload.statusComparison || emptyStatusComparisonRows(),
        ),
        bedMobility: normalizeExclusiveOption(
          loadedPayload.bedMobility,
          BED_MOBILITY_OPTIONS,
        ),
        bedMobilityComments: String(
          loadedPayload.bedMobilityComments || "",
        ).trim(),
        transferAssistLevel: normalizeExclusiveOption(
          loadedPayload.transferAssistLevel,
          TRANSFER_ASSIST_OPTIONS,
        ),
        transferComments: String(loadedPayload.transferComments || "").trim(),
        balanceStatus: normalizeExclusiveOption(
          loadedPayload.balanceStatus,
          BALANCE_STATUS_OPTIONS,
        ),
        fallRisk: normalizeExclusiveOption(
          loadedPayload.fallRisk,
          FALL_RISK_OPTIONS,
        ),
        fallPreventionEducation: normalizeExclusiveYesNo(
          loadedPayload.fallPreventionEducation,
        ),
        balanceComments: String(loadedPayload.balanceComments || "").trim(),
        muscleStrengthFindings: String(
          loadedPayload.muscleStrengthFindings || "",
        ).trim(),
        romStatus: normalizeExclusiveOption(
          loadedPayload.romStatus,
          ROM_STATUS_OPTIONS,
        ),
        romDetails: String(loadedPayload.romDetails || "").trim(),
        painLevel: parsePain(loadedPayload.painLevel),
        painLocation: String(loadedPayload.painLocation || "").trim(),
        painEducationProvided: normalizeExclusiveYesNo(
          loadedPayload.painEducationProvided,
        ),
        goalStatuses: {
          ...emptyGoalStatuses(),
          ...(loadedPayload.goalStatuses &&
          typeof loadedPayload.goalStatuses === "object" &&
          !Array.isArray(loadedPayload.goalStatuses)
            ? loadedPayload.goalStatuses
            : {}),
        },
        hepProvided: normalizeExclusiveYesNo(loadedPayload.hepProvided),
        hepInstructions: String(loadedPayload.hepInstructions || "").trim(),
        educationNarrative: String(
          loadedPayload.educationNarrative || "",
        ).trim(),
        dischargedTo: normalizeExclusiveOption(
          loadedPayload.dischargedTo,
          DISCHARGED_TO_OPTIONS,
        ),
        dischargedToOther: String(loadedPayload.dischargedToOther || "").trim(),
        additionalInstructions: String(
          loadedPayload.additionalInstructions || "",
        ).trim(),
        physicianNotified: normalizeExclusiveYesNoNotRequired(
          loadedPayload.physicianNotified,
        ),
        rnCaseManagerNotified: normalizeExclusiveYesNo(
          loadedPayload.rnCaseManagerNotified,
        ),
        communicationNotes: String(
          loadedPayload.communicationNotes || "",
        ).trim(),
        dischargeSummary: String(loadedPayload.dischargeSummary || "").trim(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      PT_DISCHARGE_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      NURSE_LEFTOVER_KEYS.forEach((key) => {
        delete merged[key];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolvePtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Physical Therapy Discharge Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            dischargeDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getPtDischargeNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const episodePrefill = buildPatientPrefill();
        const commonHeader = await resolvePtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            socDate: existingPayload.socDate || episodePrefill.socDate || "",
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateStatusComparisonRow = (parameter, patch) => {
    setValues((prev) => ({
      ...prev,
      statusComparison: (prev.statusComparison || []).map((row) =>
        row.parameter === parameter ? { ...row, ...patch } : row,
      ),
    }));
  };

  const setGoalStatus = (goal, status) => {
    setValues((prev) => ({
      ...prev,
      goalStatuses: {
        ...prev.goalStatuses,
        [goal]: status,
      },
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        PT_FORM_EDITOR_ROLES,
        "View only — only PT/PTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      dischargeDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...ptFields
    } = values;

    const base = emptyPtDischargePayload();
    const known = { ...base };
    Object.keys(base).forEach((key) => {
      if (ptFields[key] !== undefined) known[key] = ptFields[key];
    });

    const nestedPayload = stripNurseLeftovers(
      withNormalizedPtCommonHeader({
        ...known,
        dischargeDate: dischargeDate
          ? formatDateForInput(dischargeDate)
          : "",
        socDate: values.socDate ? formatDateForInput(values.socDate) : "",
        dischargeReason: String(values.dischargeReason || "").trim(),
        dischargeReasonOther: String(values.dischargeReasonOther || "").trim(),
        subjectiveReport: String(values.subjectiveReport || "").trim(),
        statusComparison: normalizeStatusComparison(values.statusComparison),
        bedMobility: String(values.bedMobility || "").trim(),
        bedMobilityComments: String(values.bedMobilityComments || "").trim(),
        transferAssistLevel: String(values.transferAssistLevel || "").trim(),
        transferComments: String(values.transferComments || "").trim(),
        balanceStatus: String(values.balanceStatus || "").trim(),
        fallRisk: String(values.fallRisk || "").trim(),
        fallPreventionEducation: normalizeExclusiveYesNo(
          values.fallPreventionEducation,
        ),
        balanceComments: String(values.balanceComments || "").trim(),
        muscleStrengthFindings: String(
          values.muscleStrengthFindings || "",
        ).trim(),
        romStatus: String(values.romStatus || "").trim(),
        romDetails: String(values.romDetails || "").trim(),
        painLevel: parsePain(values.painLevel),
        painLocation: String(values.painLocation || "").trim(),
        painEducationProvided: normalizeExclusiveYesNo(
          values.painEducationProvided,
        ),
        goalStatuses: {
          ...emptyGoalStatuses(),
          ...(values.goalStatuses && typeof values.goalStatuses === "object"
            ? values.goalStatuses
            : {}),
        },
        hepProvided: normalizeExclusiveYesNo(values.hepProvided),
        hepInstructions: String(values.hepInstructions || "").trim(),
        educationNarrative: String(values.educationNarrative || "").trim(),
        dischargedTo: String(values.dischargedTo || "").trim(),
        dischargedToOther: String(values.dischargedToOther || "").trim(),
        additionalInstructions: String(
          values.additionalInstructions || "",
        ).trim(),
        physicianNotified: normalizeExclusiveYesNoNotRequired(
          values.physicianNotified,
        ),
        rnCaseManagerNotified: normalizeExclusiveYesNo(
          values.rnCaseManagerNotified,
        ),
        communicationNotes: String(values.communicationNotes || "").trim(),
        dischargeSummary: String(values.dischargeSummary || "").trim(),
      }),
    );

    PT_DISCHARGE_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    delete nestedPayload.dischargeReasons;

    const payload = {
      flag,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updatePtDischargeNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.dischargeDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showDischargeReasonOther = values.dischargeReason === "Other";
  const showDischargedToOther = values.dischargedTo === "Other";

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Physical Therapy Discharge Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Document final clinical outcomes and discharge status for transition
            of care.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Discharge Date"
          dateValue={displayDateUS(values.dischargeDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Discharge Date
                </div>
                <InputDatePicker
                  name="dischargeDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.dischargeDate) || ""}
                  onChange={(event) =>
                    setValue("dischargeDate", event.target.value)
                  }
                  max={DISCHARGE_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <PtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7. Reason for Discharge */}
        <Section icon={AlertCircleIcon} title="7. Reason for Discharge">
          <ExclusiveChoiceGrid
            name="dischargeReason"
            options={DISCHARGE_REASON_OPTIONS}
            value={values.dischargeReason || ""}
            canEdit={canEdit}
            onChange={(opt) => {
              setValue("dischargeReason", opt);
              if (opt !== "Other") setValue("dischargeReasonOther", "");
            }}
            columns={4}
          />
          {showDischargeReasonOther ? (
            <input
              type="text"
              className={`${fieldClass} mt-1.5`}
              value={values.dischargeReasonOther || ""}
              disabled={!canEdit}
              placeholder="Specify other reason…"
              onChange={(e) => setValue("dischargeReasonOther", e.target.value)}
            />
          ) : null}
        </Section>

        {/* 8. Subjective Report */}
        <Section icon={CommentsIcon} title="8. Subjective Report">
          <Field label="Patient's Statement on Progress / Status">
            <textarea
              rows={4}
              className={textareaClass}
              value={values.subjectiveReport || ""}
              disabled={!canEdit}
              placeholder="Document patient's statement on progress and current status…"
              onChange={(e) => setValue("subjectiveReport", e.target.value)}
            />
          </Field>
        </Section>

        {/* 9. Patient Status at Discharge */}
        <Section
          icon={FileCheckIcon}
          title="9. Patient Status at Discharge"
        >
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[11px]">
              <thead>
                <tr className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-2.5 py-2 w-[28%]">Parameter</th>
                  <th className="px-2.5 py-2">Initial (Baseline)</th>
                  <th className="px-2.5 py-2">Discharge (Current)</th>
                  <th className="px-2.5 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(values.statusComparison || emptyStatusComparisonRows()).map(
                  (row) => (
                    <tr key={row.parameter} className="border-t border-slate-100">
                      <td className="px-2.5 py-1.5 font-medium text-slate-700 align-middle">
                        {row.parameter}
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.initialBaseline || ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateStatusComparisonRow(row.parameter, {
                              initialBaseline: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.dischargeCurrent || ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateStatusComparisonRow(row.parameter, {
                              dischargeCurrent: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.status || ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateStatusComparisonRow(row.parameter, {
                              status: e.target.value,
                            })
                          }
                        />
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </Section>

        {/* 10. Functional Assessment */}
        <Section icon={RunningIcon} title="10. Functional Assessment">
          <div className="space-y-4">
            <div>
              <SubHeading>Bed Mobility</SubHeading>
              <div className="mt-1.5">
                <ExclusiveChoiceGrid
                  name="bedMobility"
                  options={BED_MOBILITY_OPTIONS}
                  value={values.bedMobility || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("bedMobility", opt)}
                  columns={4}
                />
              </div>
              <div className="mt-2">
                <Field label="Comments">
                  <textarea
                    rows={2}
                    className={textareaClass}
                    value={values.bedMobilityComments || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("bedMobilityComments", e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>

            <div>
              <SubHeading>Transfers — Assist Level</SubHeading>
              <div className="mt-1.5">
                <ExclusiveChoiceGrid
                  name="transferAssistLevel"
                  options={TRANSFER_ASSIST_OPTIONS}
                  value={values.transferAssistLevel || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("transferAssistLevel", opt)}
                  columns={3}
                />
              </div>
              <div className="mt-3">
                <SubHeading>Transfer Types</SubHeading>
                <CheckboxGrid
                  options={TRANSFER_TYPE_OPTIONS}
                  values={values.transferTypes}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("transferTypes", opt)}
                  columns={4}
                />
              </div>
              <div className="mt-2">
                <Field label="Comments">
                  <textarea
                    rows={2}
                    className={textareaClass}
                    value={values.transferComments || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("transferComments", e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>
          </div>
        </Section>

        {/* 11. Balance Assessment */}
        <Section icon={TargetIcon} title="11. Balance Assessment">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <div className="min-w-0">
              <SubHeading>Balance Status</SubHeading>
              <div className="mt-1.5">
                <ExclusiveChoiceGrid
                  name="balanceStatus"
                  options={BALANCE_STATUS_OPTIONS}
                  value={values.balanceStatus || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("balanceStatus", opt)}
                  columns={3}
                />
              </div>
            </div>
            <div className="min-w-0">
              <SubHeading>Fall Risk</SubHeading>
              <div className="mt-1.5">
                <ExclusiveChoiceGrid
                  name="fallRisk"
                  options={FALL_RISK_OPTIONS}
                  value={values.fallRisk || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("fallRisk", opt)}
                  columns={3}
                />
              </div>
            </div>
            <div className="min-w-0">
              <SubHeading>Fall Prevention Education</SubHeading>
              <div className="mt-1.5">
                <ExclusiveChoiceGrid
                  name="fallPreventionEducation"
                  options={YES_NO_OPTIONS}
                  value={values.fallPreventionEducation || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("fallPreventionEducation", opt)}
                  columns={2}
                />
              </div>
            </div>
          </div>
          <div className="mt-3">
            <Field label="Comments">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.balanceComments || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("balanceComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 12. Strength / ROM */}
        <Section icon={HeartPulseIcon} title="12. Strength / ROM Assessment">
          <Field label="Muscle Strength Findings">
            <textarea
              rows={3}
              className={textareaClass}
              value={values.muscleStrengthFindings || ""}
              disabled={!canEdit}
              placeholder="Document MMT / strength findings…"
              onChange={(e) =>
                setValue("muscleStrengthFindings", e.target.value)
              }
            />
          </Field>
          <div className="mt-3">
            <SubHeading>ROM Status</SubHeading>
            <div className="mt-1.5 max-w-md">
              <ExclusiveChoiceGrid
                name="romStatus"
                options={ROM_STATUS_OPTIONS}
                value={values.romStatus || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("romStatus", opt)}
                columns={2}
              />
            </div>
          </div>
          <div className="mt-3">
            <Field label="ROM Details">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.romDetails || ""}
                disabled={!canEdit}
                placeholder="Document ROM limitations or notes…"
                onChange={(e) => setValue("romDetails", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 13. Pain Assessment */}
        <Section icon={HelpingIcon} title="13. Pain Assessment">
          <PainScalePills
            label="Pain Scale (0–10)"
            name="painLevel"
            value={values.painLevel}
            canEdit={canEdit}
            onChange={(v) => setValue("painLevel", v)}
          />
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
            <Field label="Pain Location">
              <input
                type="text"
                className={fieldClass}
                value={values.painLocation || ""}
                disabled={!canEdit}
                placeholder="Location…"
                onChange={(e) => setValue("painLocation", e.target.value)}
              />
            </Field>
            <Field label="Pain Education Provided">
              <ExclusiveChoiceGrid
                name="painEducationProvided"
                options={YES_NO_OPTIONS}
                value={values.painEducationProvided || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("painEducationProvided", opt)}
                columns={2}
              />
            </Field>
          </div>
        </Section>

        {/* 14. Therapy Goals Status */}
        <Section icon={TargetIcon} title="14. Therapy Goals Status">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[11px]">
              <thead>
                <tr className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-2.5 py-2 w-[36%]">Goal Category</th>
                  {GOAL_STATUS_OPTIONS.map((opt) => (
                    <th key={opt} className="px-2.5 py-2 text-center">
                      {opt}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {THERAPY_GOAL_ROWS.map((goal) => (
                  <tr key={goal} className="border-t border-slate-100">
                    <td className="px-2.5 py-2 font-medium text-slate-700">
                      {goal}
                    </td>
                    {GOAL_STATUS_OPTIONS.map((opt) => {
                      const checked = values.goalStatuses?.[goal] === opt;
                      return (
                        <td key={opt} className="px-2.5 py-2 text-center">
                          <input
                            type="radio"
                            name={`goal-status-${goal}`}
                            className="h-3.5 w-3.5 border-slate-300 text-indigo-500"
                            checked={checked}
                            disabled={!canEdit}
                            onChange={() => {
                              if (!canEdit) return;
                              setGoalStatus(goal, opt);
                            }}
                            onClick={(e) => {
                              if (canEdit && checked) {
                                e.preventDefault();
                                setGoalStatus(goal, "");
                              }
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* 15. Skilled PT Interventions */}
        <Section
          icon={MedicalNotesIcon}
          title="15. Skilled PT Interventions Completed During Episode"
        >
          <CheckboxGrid
            options={PT_INTERVENTION_OPTIONS}
            values={values.interventionsCompleted}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("interventionsCompleted", opt)}
            columns={3}
          />
        </Section>

        {/* 16. Home Exercise Program */}
        <Section icon={HomeIcon} title="16. Home Exercise Program">
          <div>
            <SubHeading>HEP Provided</SubHeading>
            <div className="mt-1.5 max-w-xs">
              <ExclusiveChoiceGrid
                name="hepProvided"
                options={YES_NO_OPTIONS}
                value={values.hepProvided || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("hepProvided", opt)}
                columns={2}
              />
            </div>
          </div>
          <div className="mt-3">
            <SubHeading>Patient / Caregiver Understanding</SubHeading>
            <CheckboxGrid
              options={HEP_UNDERSTANDING_OPTIONS}
              values={values.hepUnderstanding}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("hepUnderstanding", opt)}
              columns={3}
            />
          </div>
          <div className="mt-3">
            <Field label="HEP Instructions">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.hepInstructions || ""}
                disabled={!canEdit}
                placeholder="Document HEP instructions provided…"
                onChange={(e) => setValue("hepInstructions", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 17. Patient/Caregiver Education */}
        <Section icon={HelpingIcon} title="17. Patient/Caregiver Education">
          <SubHeading>Education Topics</SubHeading>
          <CheckboxGrid
            options={EDUCATION_TOPIC_OPTIONS}
            values={values.educationTopics}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("educationTopics", opt)}
            columns={2}
          />
          <div className="mt-3">
            <SubHeading>Learners</SubHeading>
            <CheckboxGrid
              options={LEARNER_OPTIONS}
              values={values.learners}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("learners", opt)}
              columns={2}
            />
          </div>
          <div className="mt-3">
            <Field label="Education Narrative">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.educationNarrative || ""}
                disabled={!canEdit}
                placeholder="Summarize education provided…"
                onChange={(e) => setValue("educationNarrative", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 18. Discharge Plan */}
        <Section icon={LogOutIcon} title="18. Discharge Plan">
          <SubHeading>Discharged To</SubHeading>
          <ExclusiveChoiceGrid
            name="dischargedTo"
            options={DISCHARGED_TO_OPTIONS}
            value={values.dischargedTo || ""}
            canEdit={canEdit}
            onChange={(opt) => {
              setValue("dischargedTo", opt);
              if (opt !== "Other") setValue("dischargedToOther", "");
            }}
            columns={3}
          />
          {showDischargedToOther ? (
            <input
              type="text"
              className={`${fieldClass} mt-1.5`}
              value={values.dischargedToOther || ""}
              disabled={!canEdit}
              placeholder="Specify other disposition…"
              onChange={(e) => setValue("dischargedToOther", e.target.value)}
            />
          ) : null}
          <div className="mt-3">
            <SubHeading>Follow-Up Recommendations</SubHeading>
            <CheckboxGrid
              options={FOLLOW_UP_OPTIONS}
              values={values.followUpRecommendations}
              canEdit={canEdit}
              onToggle={(opt) =>
                toggleListValue("followUpRecommendations", opt)
              }
              columns={2}
            />
          </div>
          <div className="mt-3">
            <Field label="Additional Instructions">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.additionalInstructions || ""}
                disabled={!canEdit}
                placeholder="Additional discharge instructions…"
                onChange={(e) =>
                  setValue("additionalInstructions", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        {/* 19. Physician / Care Team Communication */}
        <Section
          icon={CommentsIcon}
          title="19. Physician / Care Team Communication"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <SubHeading>Physician Notified</SubHeading>
              <div className="mt-1.5">
                <ExclusiveChoiceGrid
                  name="physicianNotified"
                  options={YES_NO_NOT_REQUIRED_OPTIONS}
                  value={values.physicianNotified || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("physicianNotified", opt)}
                  columns={3}
                />
              </div>
            </div>
            <div>
              <SubHeading>RN / Case Manager Notified</SubHeading>
              <div className="mt-1.5">
                <ExclusiveChoiceGrid
                  name="rnCaseManagerNotified"
                  options={YES_NO_OPTIONS}
                  value={values.rnCaseManagerNotified || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("rnCaseManagerNotified", opt)}
                  columns={2}
                />
              </div>
            </div>
          </div>
          <div className="mt-3">
            <Field label="Communication Notes">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.communicationNotes || ""}
                disabled={!canEdit}
                placeholder="Document care team communication…"
                onChange={(e) => setValue("communicationNotes", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 20. PT Discharge Summary Narrative */}
        <Section
          icon={MedicalNotesIcon}
          title="20. PT Discharge Summary Narrative"
        >
          <Field label="Final Skilled Summary / Impression">
            <textarea
              rows={6}
              className={textareaClass}
              value={values.dischargeSummary || ""}
              disabled={!canEdit}
              placeholder="Document final skilled PT summary and clinical impression…"
              onChange={(e) => setValue("dischargeSummary", e.target.value)}
            />
          </Field>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection={false}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="PT / PTA Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

PtDischargeNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default PtDischargeNoteForm;
